/* eslint-disable react-hooks/exhaustive-deps */
/**
 * LiveRideTracker.jsx
 *
 * Real-time admin view of every ride, stage-by-stage, the moment it happens —
 * no page refresh, no polling. Driven entirely by the single "admin-ride-update"
 * socket event emitted from passengerSocket.js / driverSocket.js at each stage:
 *   requested → driver_selected → accepted → declined → trip_started
 *   → trip_completed → payment_completed → rating_submitted → cancelled
 *
 * Two views:
 *   1. Live Map  (default) — pickup/drop pins + route line, same visual
 *      language as RideAnalytics' Map tab, but live instead of historical.
 *   2. Table     — every ride as a row, full detail, sortable by latest update.
 *
 * Nothing is dropped: each incoming update is merged (not replaced) into the
 * existing ride record, so a field that isn't present on a later event
 * (e.g. trip_started doesn't resend pickup/drop) doesn't wipe out what we
 * already know. A running `history` array keeps every stage transition.
 */

import { useState, useEffect, useRef, useMemo } from "react";
import {
  GoogleMap,
  LoadScript,
  Marker,
  InfoWindow,
  Polyline,
} from "@react-google-maps/api";
import { socket } from "../socket";
// ⚠️ Path கீழ adjust பண்ணுங்க — உங்க folder structure-க்கு ஏத்த மாதிரி
// (rideAnalyticsApi.js இருக்கும் actual location எது நு பாத்து)
import { getRidesForMap } from "../api/rideAnalyticsApi";

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const mapContainerStyle = {
  width: "100%",
  height: "calc(100vh - 220px)",
  borderRadius: 12,
};

const defaultCenter = {
  lat: 11.0168,
  lng: 76.9558,
};

const ROUTE_COLORS = [
  "#6366f1",
  "#ec4899",
  "#14b8a6",
  "#f97316",
  "#8b5cf6",
  "#06b6d4",
  "#e11d48",
  "#84cc16",
  "#a855f7",
  "#0ea5e9",
];

// ✅ Stage meta — color + label + whether it's a "closed" ride
const STAGE_META = {
  requested: { label: "Requested", color: "#3b82f6", terminal: false },
  driver_selected: { label: "Driver Selected", color: "#6366f1", terminal: false },
  accepted: { label: "Accepted", color: "#f59e0b", terminal: false },
  declined: { label: "Declined", color: "#94a3b8", terminal: true },
  trip_started: { label: "Trip In Progress", color: "#8b5cf6", terminal: false },
  trip_completed: { label: "Trip Completed", color: "#0ea5e9", terminal: false },
  payment_completed: { label: "Payment Received", color: "#22c55e", terminal: false },
  rating_submitted: { label: "Completed", color: "#16a34a", terminal: true },
  cancelled: { label: "Cancelled", color: "#ef4444", terminal: true },
};

const getStageMeta = (stage) =>
  STAGE_META[stage] || { label: stage || "Unknown", color: "#6b7280", terminal: false };

// ════════════════════════════════════════════════════════════════════════
// ✅ Adapter — getRidesForMap API response → LiveRideTracker's internal
// ride shape (same shape the live "admin-ride-update" socket event uses).
// This is the ONLY place API field-name guesses live — if pins/statuses
// don't show correctly, fix the mapping here, not in the render code.
// ════════════════════════════════════════════════════════════════════════

// ✅ Ride.status (DB) → this file's `stage` vocabulary. Different files in
// the codebase spell/list stages slightly differently (e.g. HomeScreen.js
// uses "canceled", single-L) — normalize everything to ONE vocabulary here.
const STATUS_TO_STAGE = {
  requested: "requested",
  driver_selected: "driver_selected",
  accepted: "accepted",
  declined: "declined",
  driver_on_the_way: "trip_started", // seen in HomeScreen.js, not in STAGE_META — treat as in-progress
  trip_started: "trip_started",
  trip_completed: "trip_completed",
  payment_completed: "payment_completed",
  rating_submitted: "rating_submitted",
  cancelled: "cancelled",
  canceled: "cancelled", // spelling variant seen in HomeScreen.js
};
const normalizeStage = (status) => STATUS_TO_STAGE[status] || status;

// ✅ Defensive lat/lng/address extraction — handles the common shapes seen
// across this codebase: {latitude,longitude}, {lat,lng}, GeoJSON {coordinates:[lng,lat]}
const extractPoint = (loc) => {
  if (!loc) return null;
  if (loc.latitude != null && loc.longitude != null) {
    return { latitude: loc.latitude, longitude: loc.longitude, address: loc.address };
  }
  if (loc.lat != null && loc.lng != null) {
    return { latitude: loc.lat, longitude: loc.lng, address: loc.address };
  }
  if (Array.isArray(loc.coordinates) && loc.coordinates.length === 2) {
    return { latitude: loc.coordinates[1], longitude: loc.coordinates[0], address: loc.address };
  }
  return null;
};

// ✅ One getRidesForMap() row → LiveRideTracker's internal ride record
const adaptApiRide = (r) => ({
  rideId: r.rideId,
  stage: normalizeStage(r.status),
  fare: r.fare,
  paymentMode: r.paymentMode,
  paymentStatus: r.paymentStatus,
  rating: r.rating,
  otp: r.otp,
  driverId: r.driverId,
  driverName: r.driver?.name,
  passengerId: r.passengerId,
  passengerName: r.passenger?.name,
  pickup: extractPoint(r.pickupLocation),
  drop: extractPoint(r.dropLocation),
  updatedAt: r.completedAt || r.createdAt,
  history: [], // API snapshot doesn't carry stage history — only live socket events add to this
});

const formatTime = (ts) => {
  if (!ts) return "—";
  return new Date(ts).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

export default function LiveRideTracker() {
  const [rides, setRides] = useState({}); // keyed by rideId
  const [viewMode, setViewMode] = useState("map"); // "map" | "table" — Live Map default
  const [stageFilter, setStageFilter] = useState("all");
  const [showClosed, setShowClosed] = useState(true);
  const [showRoutes, setShowRoutes] = useState(true);
  const [selectedRide, setSelectedRide] = useState(null);
  const [selectedMarkerType, setSelectedMarkerType] = useState(null); // "pickup" | "drop"
  const mapRef = useRef(null);

  // ════════════════════════════════════════════════════════════════════════
  // ✅ FIX: Initial hydration — DB-ல இருந்து இன்னைக்கு (today) rides எல்லாம்
  // (completed + cancelled + in-progress) fetch பண்ணி state-ஐ நிரப்புறோம்.
  // இது இல்லாம, page reload பண்ணா / வேற page போய் வந்தா state காலியா
  // ஆகிடும், அடுத்த socket event வரும் வரைக்கும் காலியா தான் காட்டும்.
  // ════════════════════════════════════════════════════════════════════════
  const [initialLoading, setInitialLoading] = useState(true);
  const [initialLoadError, setInitialLoadError] = useState(null);

  const loadTodayRides = async () => {
    setInitialLoading(true);
    setInitialLoadError(null);
    const todayStr = new Date().toISOString().split("T")[0]; // "YYYY-MM-DD"

    const res = await getRidesForMap(todayStr, "all", null, 500);

    if (res?.success && Array.isArray(res.rides)) {
      setRides((prev) => {
        const hydrated = { ...prev };
        res.rides.forEach((raw) => {
          const adapted = adaptApiRide(raw);
          if (!adapted.rideId) return;
          const existing = hydrated[adapted.rideId];
          // ✅ Live socket event ஏற்கனவே இதைவிட fresh-ஆ இருந்தா, அதை overwrite பண்ணாதே
          const existingTime = existing?.updatedAt ? new Date(existing.updatedAt).getTime() : 0;
          const adaptedTime = adapted.updatedAt ? new Date(adapted.updatedAt).getTime() : 0;
          if (!existing || adaptedTime >= existingTime) {
            hydrated[adapted.rideId] = {
              ...existing,
              ...adapted,
              history: existing?.history?.length ? existing.history : [],
            };
          }
        });
        return hydrated;
      });
    } else {
      console.log("getRidesForMap failed:", res?.error);
      setInitialLoadError(res?.error || "Failed to load today's rides");
    }
    setInitialLoading(false);
  };

  useEffect(() => {
    loadTodayRides();
  }, []);

  // ════════════════════════════════════════════════════════════════════════
  // ✅ Live socket subscription — single source of truth
  // ════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    const handleUpdate = (update) => {
      const key = update.rideId || update.passengerSocketId;
      if (!key) return;

      setRides((prev) => {
        const existing = prev[key] || { rideId: key, history: [] };
        const merged = { ...existing };

        // ✅ Merge — only overwrite fields that actually came in this event,
        // so a later stage that omits pickup/drop/fare doesn't erase them.
        Object.keys(update).forEach((k) => {
          if (update[k] !== undefined && update[k] !== null) {
            merged[k] = update[k];
          }
        });

        merged.stage = update.stage;
        merged.updatedAt = update.timestamp || new Date().toISOString();
        merged.history = [
          ...existing.history,
          { stage: update.stage, timestamp: merged.updatedAt },
        ];

        return { ...prev, [key]: merged };
      });
    };

    socket.on("admin-ride-update", handleUpdate);
    return () => socket.off("admin-ride-update", handleUpdate);
  }, []);

  // ════════════════════════════════════════════════════════════════════════
  // ✅ Derived lists
  // ════════════════════════════════════════════════════════════════════════
  const allRides = useMemo(
    () =>
      Object.values(rides).sort(
        (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt),
      ),
    [rides],
  );

  const stageCounts = useMemo(() => {
    const counts = {};
    allRides.forEach((r) => {
      counts[r.stage] = (counts[r.stage] || 0) + 1;
    });
    return counts;
  }, [allRides]);

  const visibleRides = useMemo(() => {
    return allRides.filter((r) => {
      if (!showClosed && getStageMeta(r.stage).terminal) return false;
      if (stageFilter !== "all" && r.stage !== stageFilter) return false;
      return true;
    });
  }, [allRides, showClosed, stageFilter]);

  const mapPlottableRides = visibleRides.filter(
    (r) => r.pickup?.latitude || r.drop?.latitude,
  );

  // ════════════════════════════════════════════════════════════════════════
  // ✅ Map helpers
  // ════════════════════════════════════════════════════════════════════════
  const fitMapBounds = () => {
    if (!mapRef.current || mapPlottableRides.length === 0) return;
    const bounds = new window.google.maps.LatLngBounds();
    mapPlottableRides.forEach((ride) => {
      if (ride.pickup?.latitude) {
        bounds.extend({ lat: ride.pickup.latitude, lng: ride.pickup.longitude });
      }
      if (ride.drop?.latitude) {
        bounds.extend({ lat: ride.drop.latitude, lng: ride.drop.longitude });
      }
    });
    mapRef.current.fitBounds(bounds, { padding: 60 });
  };

  useEffect(() => {
    if (viewMode === "map") setTimeout(fitMapBounds, 400);
  }, [viewMode, mapPlottableRides.length]);

  const focusRide = (ride) => {
    setSelectedRide(ride);
    setSelectedMarkerType(ride.pickup?.latitude ? "pickup" : "drop");
    if (!mapRef.current) return;
    const bounds = new window.google.maps.LatLngBounds();
    if (ride.pickup?.latitude)
      bounds.extend({ lat: ride.pickup.latitude, lng: ride.pickup.longitude });
    if (ride.drop?.latitude)
      bounds.extend({ lat: ride.drop.latitude, lng: ride.drop.longitude });
    mapRef.current.fitBounds(bounds, { padding: 80 });
  };

  // ════════════════════════════════════════════════════════════════════════
  // ✅ Render
  // ════════════════════════════════════════════════════════════════════════
  return (
    <div>
      {/* ── Header ── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 14,
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: "#0f172a" }}>
            🔴 Live Ride Tracker
          </h2>
          <p style={{ margin: "2px 0 0", fontSize: 12, color: "#64748b" }}>
            {initialLoading
              ? "Loading today's rides..."
              : initialLoadError
              ? `⚠️ ${initialLoadError} — showing live updates only`
              : "Today's rides — completed, cancelled, in-progress — updates live as trips progress"}
          </p>
        </div>

        {/* Manual refresh — re-syncs from DB in case a socket event was missed */}
        <button
          onClick={loadTodayRides}
          disabled={initialLoading}
          style={{
            padding: "7px 14px",
            borderRadius: 8,
            border: "1px solid #e2e8f0",
            background: "#fff",
            fontSize: 12,
            fontWeight: 700,
            color: "#475569",
            cursor: initialLoading ? "default" : "pointer",
            opacity: initialLoading ? 0.6 : 1,
          }}
        >
          {initialLoading ? "⏳ Refreshing..." : "🔄 Refresh"}
        </button>

        {/* View toggle */}
        <div
          style={{
            display: "flex",
            background: "#f1f5f9",
            borderRadius: 10,
            padding: 4,
            gap: 4,
          }}
        >
          <button
            onClick={() => setViewMode("map")}
            style={{
              padding: "7px 16px",
              borderRadius: 8,
              border: "none",
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              background: viewMode === "map" ? "#fff" : "transparent",
              color: viewMode === "map" ? "#0f172a" : "#64748b",
              boxShadow: viewMode === "map" ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
            }}
          >
            🗺️ Live Map
          </button>
          <button
            onClick={() => setViewMode("table")}
            style={{
              padding: "7px 16px",
              borderRadius: 8,
              border: "none",
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              background: viewMode === "table" ? "#fff" : "transparent",
              color: viewMode === "table" ? "#0f172a" : "#64748b",
              boxShadow: viewMode === "table" ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
            }}
          >
            📋 Table
          </button>
        </div>
      </div>

      {/* ── Stage filter pills ── */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
        <span
          onClick={() => setStageFilter("all")}
          style={{
            padding: "5px 12px",
            borderRadius: 20,
            fontSize: 11,
            fontWeight: 700,
            cursor: "pointer",
            background: stageFilter === "all" ? "#0f172a" : "#0f172a15",
            color: stageFilter === "all" ? "#fff" : "#0f172a",
          }}
        >
          All: {allRides.length}
        </span>
        {Object.entries(stageCounts).map(([stage, count]) => {
          const meta = getStageMeta(stage);
          return (
            <span
              key={stage}
              onClick={() => setStageFilter(stageFilter === stage ? "all" : stage)}
              style={{
                padding: "5px 12px",
                borderRadius: 20,
                fontSize: 11,
                fontWeight: 700,
                cursor: "pointer",
                background: stageFilter === stage ? meta.color : `${meta.color}15`,
                color: stageFilter === stage ? "#fff" : meta.color,
                border: `1px solid ${meta.color}40`,
              }}
            >
              {meta.label}: {count}
            </span>
          );
        })}

        <label
          style={{
            marginLeft: "auto",
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 12,
            color: "#475569",
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={showClosed}
            onChange={(e) => setShowClosed(e.target.checked)}
          />
          Show completed / cancelled
        </label>
      </div>

      {/* ══════════════════════ LIVE MAP VIEW ══════════════════════ */}
      {viewMode === "map" && (
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 10,
              flexWrap: "wrap",
              gap: 10,
            }}
          >
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <button
                onClick={fitMapBounds}
                style={{
                  padding: "6px 14px",
                  borderRadius: 8,
                  border: "1px solid #e2e8f0",
                  background: "#fff",
                  color: "#475569",
                  fontWeight: 600,
                  fontSize: 12,
                  cursor: "pointer",
                }}
              >
                📍 Fit All
              </button>
              <label
                style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#475569", cursor: "pointer" }}
              >
                <input
                  type="checkbox"
                  checked={showRoutes}
                  onChange={(e) => setShowRoutes(e.target.checked)}
                />
                Show routes
              </label>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 14, fontSize: 12, color: "#64748b" }}>
              <span>🟢 Pickup</span>
              <span>🔴 Drop</span>
              <span>〰️ Route</span>
              <span style={{ fontWeight: 700, color: "#0f172a" }}>
                On map: {mapPlottableRides.length}
              </span>
            </div>
          </div>

          <div style={{ display: "flex", gap: 16 }}>
            {/* ===== MAP ===== */}
            <div style={{ flex: 1 }}>
              {!GOOGLE_KEY ? (
                <div
                  style={{
                    ...mapContainerStyle,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#f8fafc",
                    color: "#94a3b8",
                    fontSize: 13,
                  }}
                >
                  Google Maps API key missing (REACT_APP_GOOGLE_MAPS_API_KEY)
                </div>
              ) : (
                <LoadScript googleMapsApiKey={GOOGLE_KEY}>
                  <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={defaultCenter}
                    zoom={12}
                    onLoad={(map) => {
                      mapRef.current = map;
                      setTimeout(fitMapBounds, 500);
                    }}
                    options={{
                      styles: [{ featureType: "poi", stylers: [{ visibility: "off" }] }],
                    }}
                  >
                    {mapPlottableRides.map((ride, index) => {
                      const meta = getStageMeta(ride.stage);
                      return (
                        <div key={ride.rideId || index}>
                          {/* 🟢 Pickup Marker */}
                          {ride.pickup?.latitude && (
                            <Marker
                              position={{ lat: ride.pickup.latitude, lng: ride.pickup.longitude }}
                              icon={{
                                path: window.google.maps.SymbolPath.CIRCLE,
                                scale: 8,
                                fillColor: "#22c55e",
                                fillOpacity: 1,
                                strokeColor: "#fff",
                                strokeWeight: 2,
                              }}
                              onClick={() => {
                                setSelectedRide(ride);
                                setSelectedMarkerType("pickup");
                              }}
                            />
                          )}

                          {/* 🔴 Drop Marker */}
                          {ride.drop?.latitude && (
                            <Marker
                              position={{ lat: ride.drop.latitude, lng: ride.drop.longitude }}
                              icon={{
                                path: window.google.maps.SymbolPath.CIRCLE,
                                scale: 8,
                                fillColor: "#ef4444",
                                fillOpacity: 1,
                                strokeColor: "#fff",
                                strokeWeight: 2,
                              }}
                              onClick={() => {
                                setSelectedRide(ride);
                                setSelectedMarkerType("drop");
                              }}
                            />
                          )}

                          {/* 🟣 Live-stage marker (driver location if we have it, else pickup) */}
                          {!meta.terminal && ride.driverLocation?.latitude && (
                            <Marker
                              position={{
                                lat: ride.driverLocation.latitude,
                                lng: ride.driverLocation.longitude,
                              }}
                              icon={{
                                path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                                scale: 5,
                                fillColor: meta.color,
                                fillOpacity: 1,
                                strokeColor: "#fff",
                                strokeWeight: 1.5,
                              }}
                              onClick={() => {
                                setSelectedRide(ride);
                                setSelectedMarkerType("pickup");
                              }}
                            />
                          )}

                          {/* 〰️ Route Line */}
                          {showRoutes && ride.pickup?.latitude && ride.drop?.latitude && (
                            <Polyline
                              path={[
                                { lat: ride.pickup.latitude, lng: ride.pickup.longitude },
                                { lat: ride.drop.latitude, lng: ride.drop.longitude },
                              ]}
                              options={{
                                strokeColor: ROUTE_COLORS[index % ROUTE_COLORS.length],
                                strokeOpacity: 0.6,
                                strokeWeight: 3,
                                geodesic: true,
                              }}
                            />
                          )}
                        </div>
                      );
                    })}

                    {/* 🪧 InfoWindow */}
                    {selectedRide && selectedMarkerType && (
                      <InfoWindow
                        position={
                          selectedMarkerType === "pickup"
                            ? {
                                lat: selectedRide.pickup?.latitude,
                                lng: selectedRide.pickup?.longitude,
                              }
                            : {
                                lat: selectedRide.drop?.latitude,
                                lng: selectedRide.drop?.longitude,
                              }
                        }
                        onCloseClick={() => {
                          setSelectedRide(null);
                          setSelectedMarkerType(null);
                        }}
                      >
                        <RideInfoCard ride={selectedRide} />
                      </InfoWindow>
                    )}
                  </GoogleMap>
                </LoadScript>
              )}
            </div>

            {/* ===== LIVE RIDE LIST SIDEBAR ===== */}
            <div
              style={{
                width: 320,
                background: "#fff",
                borderRadius: 12,
                border: "1px solid #e2e8f0",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                maxHeight: "calc(100vh - 220px)",
              }}
            >
              <div style={{ padding: "14px 16px", borderBottom: "1px solid #e2e8f0", background: "#f8fafc" }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", margin: 0 }}>
                  Live Rides ({visibleRides.length})
                </h4>
                <p style={{ fontSize: 11, color: "#94a3b8", margin: 0 }}>Click to zoom on map</p>
              </div>
              <div style={{ flex: 1, overflowY: "auto" }}>
                {visibleRides.length === 0 ? (
                  <div style={{ padding: 30, textAlign: "center", color: "#94a3b8" }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>🗺️</div>
                    <p style={{ fontSize: 13 }}>Waiting for ride activity...</p>
                  </div>
                ) : (
                  visibleRides.map((ride, i) => {
                    const meta = getStageMeta(ride.stage);
                    return (
                      <div
                        key={ride.rideId || i}
                        onClick={() => focusRide(ride)}
                        style={{
                          padding: "12px 14px",
                          borderBottom: "1px solid #f1f5f9",
                          cursor: "pointer",
                          background: selectedRide?.rideId === ride.rideId ? "#eef2ff" : "transparent",
                          borderLeft:
                            selectedRide?.rideId === ride.rideId
                              ? "3px solid #6366f1"
                              : "3px solid transparent",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                          <span style={{ fontWeight: 700, fontSize: 12, color: "#0f172a" }}>
                            {ride.rideId || "pending..."}
                          </span>
                          <span
                            style={{
                              background: `${meta.color}15`,
                              color: meta.color,
                              padding: "2px 8px",
                              borderRadius: 10,
                              fontSize: 10,
                              fontWeight: 700,
                            }}
                          >
                            {meta.label}
                          </span>
                        </div>

                        <div style={{ fontSize: 11, color: "#475569", display: "flex", gap: 6, marginBottom: 3 }}>
                          <span style={{ color: "#22c55e", fontSize: 10 }}>●</span>
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 250 }}>
                            {ride.pickup?.address || "N/A"}
                          </span>
                        </div>
                        <div style={{ fontSize: 11, color: "#475569", display: "flex", gap: 6, marginBottom: 6 }}>
                          <span style={{ color: "#ef4444", fontSize: 10 }}>●</span>
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 250 }}>
                            {ride.drop?.address || "N/A"}
                          </span>
                        </div>

                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}>
                          <div style={{ display: "flex", gap: 8, color: "#94a3b8" }}>
                            {ride.driverName && <span>🛺 {ride.driverName}</span>}
                            {!ride.driverName && ride.driverId && <span>🛺 {ride.driverId}</span>}
                          </div>
                          <div style={{ display: "flex", gap: 8 }}>
                            {ride.fare > 0 && (
                              <span style={{ fontWeight: 700, color: "#059669" }}>₹{Math.round(ride.fare)}</span>
                            )}
                            <span style={{ color: "#94a3b8" }}>{formatTime(ride.updatedAt)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════ TABLE VIEW ══════════════════════ */}
      {viewMode === "table" && (
        <div
          style={{
            background: "#fff",
            borderRadius: 12,
            border: "1px solid #e2e8f0",
            overflow: "hidden",
          }}
        >
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  {["Ride ID", "Stage", "Passenger", "Driver", "Pickup", "Drop", "Fare", "Payment", "OTP", "Updated"].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: "left",
                        padding: "10px 12px",
                        borderBottom: "2px solid #e2e8f0",
                        color: "#374151",
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleRides.map((ride) => {
                  const meta = getStageMeta(ride.stage);
                  return (
                    <tr key={ride.rideId} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "10px 12px", fontWeight: 600, color: "#0f172a" }}>
                        {ride.rideId || "—"}
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <span
                          style={{
                            background: `${meta.color}15`,
                            color: meta.color,
                            padding: "3px 10px",
                            borderRadius: 999,
                            fontSize: 11,
                            fontWeight: 700,
                          }}
                        >
                          {meta.label}
                        </span>
                      </td>
                      <td style={{ padding: "10px 12px", color: "#475569" }}>{ride.passengerId || "—"}</td>
                      <td style={{ padding: "10px 12px", color: "#475569" }}>
                        {ride.driverName || ride.driverId || "—"}
                      </td>
                      <td style={{ padding: "10px 12px", color: "#475569", maxWidth: 220 }}>
                        {ride.pickup?.address || "—"}
                      </td>
                      <td style={{ padding: "10px 12px", color: "#475569", maxWidth: 220 }}>
                        {ride.drop?.address || "—"}
                      </td>
                      <td style={{ padding: "10px 12px", fontWeight: 700, color: "#059669" }}>
                        {ride.fare ? `₹${Math.round(ride.fare)}` : "—"}
                      </td>
                      <td style={{ padding: "10px 12px", color: "#475569" }}>
                        {ride.paymentMode || "—"}
                      </td>
                      <td style={{ padding: "10px 12px", color: "#475569" }}>{ride.otp || "—"}</td>
                      <td style={{ padding: "10px 12px", color: "#94a3b8" }}>{formatTime(ride.updatedAt)}</td>
                    </tr>
                  );
                })}
                {visibleRides.length === 0 && (
                  <tr>
                    <td colSpan={10} style={{ padding: 30, textAlign: "center", color: "#94a3b8" }}>
                      Waiting for ride activity...
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// ✅ Shared InfoWindow content — full ride detail, nothing left out
// ════════════════════════════════════════════════════════════════════════
function RideInfoCard({ ride }) {
  const meta = getStageMeta(ride.stage);
  return (
    <div style={{ width: 270, fontSize: 12, lineHeight: 1.6 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>{ride.rideId}</span>
        <span
          style={{
            background: `${meta.color}20`,
            color: meta.color,
            padding: "3px 10px",
            borderRadius: 12,
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          {meta.label}
        </span>
      </div>

      <div style={{ padding: "6px 8px", background: "#f0fdf4", borderRadius: 6, marginBottom: 6, borderLeft: "3px solid #22c55e" }}>
        <div style={{ fontWeight: 600, color: "#15803d", fontSize: 10 }}>🟢 PICKUP</div>
        <div style={{ color: "#475569", fontSize: 11 }}>{ride.pickup?.address || "N/A"}</div>
      </div>

      <div style={{ padding: "6px 8px", background: "#fef2f2", borderRadius: 6, marginBottom: 8, borderLeft: "3px solid #ef4444" }}>
        <div style={{ fontWeight: 600, color: "#dc2626", fontSize: 10 }}>🔴 DROP</div>
        <div style={{ color: "#475569", fontSize: 11 }}>{ride.drop?.address || "N/A"}</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, background: "#f8fafc", padding: 8, borderRadius: 6 }}>
        {ride.fare > 0 && (
          <div>
            <span style={{ color: "#94a3b8", fontSize: 10 }}>Fare</span>
            <div style={{ fontWeight: 700, color: "#059669" }}>₹{Math.round(ride.fare)}</div>
          </div>
        )}
        {ride.paymentMode && (
          <div>
            <span style={{ color: "#94a3b8", fontSize: 10 }}>Payment</span>
            <div style={{ fontWeight: 600, color: "#475569", textTransform: "capitalize" }}>
              {ride.paymentMode === "cash" ? "💵" : "💳"} {ride.paymentMode}
            </div>
          </div>
        )}
        {(ride.driverName || ride.driverId) && (
          <div>
            <span style={{ color: "#94a3b8", fontSize: 10 }}>Driver</span>
            <div style={{ fontWeight: 600, color: "#475569" }}>🛺 {ride.driverName || ride.driverId}</div>
          </div>
        )}
        {ride.passengerId && (
          <div>
            <span style={{ color: "#94a3b8", fontSize: 10 }}>Passenger</span>
            <div style={{ fontWeight: 600, color: "#475569" }}>👤 {ride.passengerId}</div>
          </div>
        )}
        {ride.otp && (
          <div>
            <span style={{ color: "#94a3b8", fontSize: 10 }}>OTP</span>
            <div style={{ fontWeight: 700, color: "#7C3AED" }}>{ride.otp}</div>
          </div>
        )}
        {ride.rating > 0 && (
          <div>
            <span style={{ color: "#94a3b8", fontSize: 10 }}>Rating</span>
            <div style={{ fontWeight: 600, color: "#f59e0b" }}>⭐ {ride.rating}</div>
          </div>
        )}
        <div>
          <span style={{ color: "#94a3b8", fontSize: 10 }}>Updated</span>
          <div style={{ fontWeight: 600, color: "#475569" }}>🕐 {formatTime(ride.updatedAt)}</div>
        </div>
      </div>
    </div>
  );
}